import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Download,
  Loader2,
  RefreshCw,
  Wallet,
} from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { api, ApiRequestError } from "../lib/api";
import { formatDateTime, formatRupiah } from "../lib/format";

type FeeRule = {
  mode: string;
  amount: number;
  active: boolean;
};

type LedgerEntry = {
  id: number;
  entryType: string;
  direction: string;
  amount: number;
  balanceAfter: number;
  note: string | null;
  createdAt: string;
};

type DepositTopup = {
  id: string;
  amount: number;
  totalAmount: number;
  status: string;
  providerInvoiceId: string | null;
  qrisPayload: string | null;
  qrDataUrl: string | null;
  expiresAt: string | null;
  paidAt: string | null;
  createdAt: string;
};

type BillingResponse = {
  balance: number;
  billingType: string;
  feeRule: FeeRule | null;
  topupEnabled: boolean;
  entries: LedgerEntry[];
  topups: DepositTopup[];
};

const ENTRY_LABELS: Record<string, string> = {
  deposit: "Top-up deposit",
  fee: "Fee transaksi",
  refund: "Pengembalian fee",
  adjustment: "Koreksi saldo",
};

const PRESETS = [50_000, 100_000, 250_000, 500_000];

export function OwnerBillingPage() {
  const [data, setData] = useState<BillingResponse | null>(null);
  const [amount, setAmount] = useState("100000");
  const [activeTopup, setActiveTopup] = useState<DepositTopup | null>(null);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api<BillingResponse>("/api/owner/billing");
      setData(response);
      const pending = response.topups.find((topup) => topup.status === "pending");
      setActiveTopup(pending ?? null);
      setError(null);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat billing deposit.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!activeTopup || activeTopup.status !== "pending") return;
    const timer = window.setInterval(async () => {
      try {
        const response = await api<{
          topup: DepositTopup;
          balance: number;
        }>(`/api/owner/billing/topup/${activeTopup.id}/status`);
        setActiveTopup(response.topup);
        setData((current) =>
          current ? { ...current, balance: response.balance } : current,
        );
        if (response.topup.status === "paid") {
          setNotice("Top-up deposit berhasil. Saldo sudah diperbarui.");
          void load();
        }
      } catch {
        // polling tenang; error sesekali tidak perlu mengganggu halaman
      }
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [activeTopup?.id, activeTopup?.status, load]);

  const expiresIn = activeTopup?.expiresAt
    ? new Date(activeTopup.expiresAt).getTime() - now
    : null;
  const topupExpired =
    activeTopup?.status === "expired" ||
    (expiresIn !== null && expiresIn <= 0);
  const qrVisible =
    activeTopup?.status === "pending" &&
    !topupExpired &&
    Boolean(activeTopup.qrDataUrl);

  const feeLabel = useMemo(() => {
    if (data?.billingType !== "paid") return "Bebas fee";
    if (!data?.feeRule) return "Belum diatur";
    if (!data.feeRule.active) return "Nonaktif";
    return `${formatRupiah(data.feeRule.amount)} / order`;
  }, [data?.billingType, data?.feeRule]);

  async function createTopup() {
    const value = Number(amount);
    if (!Number.isInteger(value) || value < 10_000) {
      setError("Nominal top-up minimal Rp10.000.");
      return;
    }
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      const response = await api<{ topup: DepositTopup }>(
        "/api/owner/billing/topup",
        {
          method: "POST",
          body: JSON.stringify({ amount: value }),
        },
      );
      setActiveTopup(response.topup);
      setData((current) =>
        current
          ? { ...current, topups: [response.topup, ...current.topups].slice(0, 5) }
          : current,
      );
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal membuat QRIS top-up.",
      );
    } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div className="page-stack">
        <PageHeader
          eyebrow="Keuangan"
          title="Billing & deposit"
          description="Saldo deposit, fee transaksi, dan top-up."
        />
        <div className="card card--pad">
          <span className="skeleton skeleton--title" />
          <span className="skeleton skeleton--block" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Keuangan"
        title="Billing & deposit"
        description={
          data?.billingType === "paid"
            ? "Fee platform dipotong dari saldo deposit setiap transaksi berhasil."
            : "Tenant ini memakai billing khusus; tidak ada fee per transaksi."
        }
        actions={
          <button type="button" className="btn btn-secondary" onClick={() => void load()}>
            <RefreshCw size={16} /> Muat ulang
          </button>
        }
      />

      {error ? (
        <div className="alert alert--danger" role="alert">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="alert alert--success" role="status">
          {notice}
        </div>
      ) : null}

      <section className="stat-grid">
        <article className="stat-card">
          <span className="stat-card__icon stat-card__icon--accent">
            <Wallet size={18} />
          </span>
          <div>
            <p className="stat-card__label">Saldo deposit</p>
            <strong className="stat-card__value">
              {formatRupiah(data?.balance ?? 0)}
            </strong>
            <span className="stat-card__note">
              {data?.billingType === "paid"
                ? "Dipoton fee per transaksi"
                : "Billing khusus / bebas fee"}
            </span>
          </div>
        </article>
        <article className="stat-card">
          <span className="stat-card__icon">
            <ArrowDownLeft size={18} />
          </span>
          <div>
            <p className="stat-card__label">Fee per order</p>
            <strong className="stat-card__value">{feeLabel}</strong>
            <span className="stat-card__note">
              {data?.billingType === "paid" ? "Billing berbayar" : "Billing khusus"}
            </span>
          </div>
        </article>
        <article className="stat-card">
          <span className="stat-card__icon">
            <ArrowUpRight size={18} />
          </span>
          <div>
            <p className="stat-card__label">Top-up tersedia</p>
            <strong className="stat-card__value">
              {data?.topupEnabled ? "Aktif" : "Belum aktif"}
            </strong>
            <span className="stat-card__note">
              {data?.topupEnabled
                ? "QRIS merchant platform"
                : "Hubungi superadmin"}
            </span>
          </div>
        </article>
      </section>

      <div className="settings-grid">
        <section className="card card--pad">
          <header className="report-card__head">
            <div>
              <p className="eyebrow">Top-up</p>
              <h2>Isi saldo deposit</h2>
              <p className="section-subtitle">
                Bayar QRIS ke merchant platform. Saldo masuk otomatis setelah
                pembayaran terverifikasi.
              </p>
            </div>
            <Wallet size={20} />
          </header>

          {!data?.topupEnabled ? (
            <div className="alert alert--warning">
              Top-up QRIS belum diaktifkan superadmin. Hubungi superadmin untuk
              menambah saldo deposit secara manual.
            </div>
          ) : (
            <>
              <div className="topup-presets">
                {PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={`filter-chip${Number(amount) === preset ? " is-active" : ""}`}
                    onClick={() => setAmount(String(preset))}
                  >
                    {formatRupiah(preset)}
                  </button>
                ))}
              </div>
              <label className="field">
                <span>Nominal top-up</span>
                <input
                  className="input"
                  type="number"
                  min={10_000}
                  step={1_000}
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                />
              </label>
              <div className="card-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void createTopup()}
                  disabled={creating || Number(amount) < 10_000}
                >
                  {creating ? (
                    <Loader2 className="spin" size={16} />
                  ) : (
                    <Wallet size={16} />
                  )}
                  Buat QRIS top-up
                </button>
              </div>
            </>
          )}

          {activeTopup ? (
            <div className="topup-active">
              <div className="section-heading section-heading--compact">
                <div>
                  <p className="eyebrow">Top-up</p>
                  <h3>{formatRupiah(activeTopup.amount)}</h3>
                  <p className="section-subtitle">
                    Status: {activeTopup.status}
                    {expiresIn !== null && activeTopup.status === "pending" && !topupExpired
                      ? ` · ${Math.max(0, Math.ceil(expiresIn / 1000))} detik`
                      : ""}
                  </p>
                </div>
              </div>

              {qrVisible ? (
                <div className="qr-card">
                  <img src={activeTopup.qrDataUrl ?? ""} alt="QRIS top-up deposit" />
                  <div className="qr-card__amount">
                    <span>Total bayar</span>
                    <strong>{formatRupiah(activeTopup.totalAmount)}</strong>
                  </div>
                  <div className="qr-card__actions">
                    <a
                      className="btn btn-secondary"
                      href={activeTopup.qrDataUrl ?? "#"}
                      download={`QRIS-topup-${activeTopup.id}.png`}
                    >
                      <Download size={15} /> Unduh QRIS
                    </a>
                  </div>
                  <p className="fine-print qr-card__hint">
                    Halaman ini mengecek status otomatis. Jangan tutup sebelum
                    pembayaran terverifikasi.
                  </p>
                </div>
              ) : activeTopup.status === "paid" ? (
                <div className="alert alert--success">
                  Top-up sudah dibayar. Saldo deposit bertambah.
                </div>
              ) : (
                <div className="alert alert--warning">
                  QRIS top-up sudah kedaluwarsa. Buat QRIS baru untuk melanjutkan.
                </div>
              )}
            </div>
          ) : null}
        </section>

        <section className="card card--pad">
          <header className="report-card__head">
            <div>
              <p className="eyebrow">Riwayat</p>
              <h2>Mutasi deposit</h2>
            </div>
            <ArrowUpRight size={20} />
          </header>

          {data && data.entries.length > 0 ? (
            <div className="ledger-list">
              {data.entries.map((entry) => (
                <article className="ledger-row" key={entry.id}>
                  <span
                    className={`ledger-row__icon ledger-row__icon--${entry.direction}`}
                  >
                    {entry.direction === "credit" ? (
                      <ArrowDownLeft size={15} />
                    ) : (
                      <ArrowUpRight size={15} />
                    )}
                  </span>
                  <div className="ledger-row__body">
                    <strong>
                      {ENTRY_LABELS[entry.entryType] ?? entry.entryType}
                    </strong>
                    <span>{entry.note ?? "Tanpa catatan"}</span>
                    <small>{formatDateTime(entry.createdAt)}</small>
                  </div>
                  <div className="ledger-row__amount">
                    <strong>
                      {entry.direction === "credit" ? "+" : "-"}
                      {formatRupiah(entry.amount)}
                    </strong>
                    <span>Saldo {formatRupiah(entry.balanceAfter)}</span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <span className="empty-state__icon">
                <Wallet size={24} />
              </span>
              <h3>Belum ada mutasi</h3>
              <p>Top-up atau fee transaksi akan muncul di sini.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
